import React, { lazy } from "react";
import {
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { useTranslate } from "../i18n";
import ProtectedRoute from "../authentications/ProtectedRoute";
import { CategoryProvider } from "../components/CategoryContext";
import AuroraBackground from "../components/AuroraBackground";
import MainLayout from "../layout/MainLayout";
import Login from "../authentications/Login";

const ResetPassword = lazy(() => import("../authentications/ResetPassword"));
const NewPassword = lazy(() => import("../authentications/NewPassword"));
const Signup = lazy(() => import("../authentications/Signup"));
const Hero = lazy(() => import("../pages/Side-menu/Hero"));
const PostContent = lazy(() => import("../pages/Side-menu/PostContent"));
const Hours = lazy(() => import("../pages/Side-menu/Hours"));
const PriceList = lazy(() => import("../pages/Side-menu/PriceList"));
const Employee = lazy(() => import("../pages/Side-menu/Employee"));
const Category = lazy(() => import("../pages/Side-menu/Category"));
const Users = lazy(() => import("../pages/Side-menu/Users"));
const Roles = lazy(() => import("../pages/Side-menu/Roles"));
const Tenants = lazy(() => import("../pages/Side-menu/Tenants"));
const Configs = lazy(() => import("../pages/Side-menu/Configs"));
const Translations = lazy(() => import("../pages/Side-menu/Translations"));
const Gallery = lazy(() => import("../pages/Side-menu/Gallery"));
const Profile = lazy(() => import("../pages/Side-menu/Profile"));
const AuditLog = lazy(() => import("../pages/Side-menu/AuditLog"));
const ServiceCatalogue = lazy(() => import("../pages/Side-menu/ServiceCatalogue"));
const Inquiries = lazy(() => import("../pages/Side-menu/Inquiries"));
const Testimonials = lazy(() => import("../pages/Side-menu/Testimonials"));
const BookingSettings = lazy(() => import("../pages/Side-menu/BookingSettings"));
const Bookings = lazy(() => import("../pages/Side-menu/Bookings"));
const BookingCalendar = lazy(() => import("../pages/Side-menu/BookingCalendar"));
const BookingLab = lazy(() => import("../pages/Side-menu/BookingLab"));
const BookingWidget = lazy(() => import("../pages/BookingWidget"));
const BookingManage = lazy(() => import("../pages/BookingManage"));
const TestimonialSubmit = lazy(() => import("../pages/TestimonialSubmit"));

const SLUG_MAP = {
  employee: Employee,
  employees: Employee,
  zamestnanci: Employee,
  zamestnanec: Employee,
  posts: PostContent,
  post: PostContent,
  prispevky: PostContent,
  prispevek: PostContent,
  hours: Hours,
  hour: Hours,
  hodiny: Hours,
  oteviraci_doba: Hours,
  "oteviraci-doba": Hours,
  pricelist: PriceList,
  "price-list": PriceList,
  cenik: PriceList,
  gallery: Gallery,
  galerie: Gallery,
  category: Category,
  kategorie: Category,
  categories: Category,
  users: Users,
  user: Users,
  "user-management": Users,
  "sprava-uzivatelu": Users,
  uzivatele: Users,
  roles: Roles,
  role: Roles,
  oprávnění: Roles,
  opravneni: Roles,
  "role-management": Roles,
  tenants: Tenants,
  tenant: Tenants,
  najemci: Tenants,
  najemce: Tenants,
  configs: Configs,
  config: Configs,
  konfigurace: Configs,
  translations: Translations,
  translation: Translations,
  preklady: Translations,
  preklad: Translations,
  profile: Profile,
  profil: Profile,
  audit: AuditLog,
  "audit-log": AuditLog,
  "nav-audit": AuditLog,
  "service-catalogue": ServiceCatalogue,
  "service-catalog": ServiceCatalogue,
  services: ServiceCatalogue,
  sluzby: ServiceCatalogue,
  procedury: ServiceCatalogue,
  osetreni: ServiceCatalogue,
  "nav-service-catalogue": ServiceCatalogue,
  inquiries: Inquiries,
  inquiry: Inquiries,
  dotazy: Inquiries,
  dotaz: Inquiries,
  "nav-inquiries": Inquiries,
  leady: Inquiries,
  testimonials: Testimonials,
  testimonial: Testimonials,
  recenze: Testimonials,
  "booking-settings": BookingSettings,
  "nastaveni-rezervaci": BookingSettings,
  rezervace: Bookings,
  "rezervace-kalendar": BookingCalendar,
  "booking-calendar": BookingCalendar,
  "booking-lab": BookingLab,
};

function SlugPage() {
  const { slug } = useParams();
  const Component = SLUG_MAP[slug?.toLowerCase()];

  return Component ? <Component /> : <Navigate to="/" replace />;
}

function ProtectedAppShell() {
  return (
    <CategoryProvider>
      <MainLayout />
    </CategoryProvider>
  );
}

function NotFoundPage() {
  const { t } = useTranslate();

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--page-bg)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        position: "relative",
      }}
    >
      <AuroraBackground intensity={0.7} />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 16,
        }}
      >
        <img
          src={process.env.PUBLIC_URL + "/assets/svg/eclipse.svg"}
          alt={t("shared.brand.logoAlt")}
          style={{ width: 32, height: 32, opacity: 0.4 }}
        />
        <div
          style={{
            fontSize: 72,
            fontWeight: 800,
            color: "rgb(var(--accent-rgb) / 0.14)",
            letterSpacing: "-0.04em",
            lineHeight: 1,
          }}
        >
          404
        </div>
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: "var(--muted2)",
            marginTop: -8,
          }}
        >
          {t("layout.notFound.title")}
        </div>
        <div style={{ fontSize: 13, color: "var(--muted)" }}>
          {t("layout.notFound.description")}
        </div>
        <a
          href="/"
          style={{
            marginTop: 8,
            background: "var(--accent-g)",
            border: "1px solid var(--accent-soft-border)",
            borderRadius: 8,
            padding: "9px 20px",
            color: "var(--accent)",
            fontSize: 13,
            fontWeight: 500,
            textDecoration: "none",
          }}
        >
          {t("layout.notFound.backToDashboard")}
        </a>
      </div>
    </div>
  );
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/resetpassword" element={<ResetPassword />} />
      <Route path="/newpassword" element={<NewPassword />} />
      <Route path="/submit/:token" element={<TestimonialSubmit />} />
      <Route path="/book/:tenantSlug" element={<BookingWidget />} />
      <Route path="/booking/:token" element={<BookingManage />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<ProtectedAppShell />}>
          <Route index element={<Hero />} />
          <Route path="posts" element={<PostContent />} />
          <Route path="hours" element={<Hours />} />
          <Route path="pricelist" element={<PriceList />} />
          <Route path="employee" element={<Employee />} />
          <Route path="category" element={<Category />} />
          <Route path="users" element={<Users />} />
          <Route path="roles" element={<Roles />} />
          <Route path="tenants" element={<Tenants />} />
          <Route path="configs" element={<Configs />} />
          <Route path="translations" element={<Translations />} />
          <Route path="gallery" element={<Gallery />} />
          <Route path="profile" element={<Profile />} />
          <Route path="audit" element={<AuditLog />} />
          <Route path="service-catalogue" element={<ServiceCatalogue />} />
          <Route path="inquiries" element={<Inquiries />} />
          <Route path="testimonials" element={<Testimonials />} />
          <Route path="booking-settings" element={<BookingSettings />} />
          <Route path="rezervace" element={<Bookings />} />
          <Route path="rezervace-kalendar" element={<BookingCalendar />} />
          <Route path="booking-lab" element={<BookingLab />} />
          <Route path=":slug" element={<SlugPage />} />
        </Route>
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
